import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { dispatchOnDocumentChange } from '@ps-int/ufo-rocks/lib/helpers/document';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
    getHideRecordingLimitPopup,
    getScreenRecordingLimit,
} from '../../../../../../shared/features/config/selectors';
import { getIsShownLimitMessage } from '../../../../../../shared/features/settings';
import { useActions } from '../../../../../../shared/hooks';
import {
    isNonProductionEnvironment as selectIsNonProductionEnvironment,
    selectRecordStartTime,
} from '../../../../selectors';
import { stopRecordConferenceAction } from '../../../record/slice';
import RecordLimitationPopup from '../../components/record-limitation-popup';
import { BaseStopRecordingButton } from '../../toolbar-buttons/base-stop-recording-button';
import classes from './style.css';

type RecordButtonProps = {
    isShowLimitationPopup: boolean;
    toggleLimitationPopup: () => void;
};

export function LocalRecordingButton({ isShowLimitationPopup, toggleLimitationPopup }: RecordButtonProps) {
    const isShownLimitMessage = useSelector(getIsShownLimitMessage);
    const isNonProductionEnvironment = useSelector(selectIsNonProductionEnvironment);
    const screenRecordingLimit = useSelector(getScreenRecordingLimit);
    const hideRecordingLimitPopup = useSelector(getHideRecordingLimitPopup);
    const recordStartTime = useSelector(selectRecordStartTime);

    const actions = useActions({ stop: stopRecordConferenceAction });

    const ref = useRef<HTMLDivElement>(null);

    const { search } = useLocation();

    const limitSeconds = useMemo(() => {
        if (isNonProductionEnvironment) {
            const seconds = new URLSearchParams(search).get('testplane_limit_record');

            if (seconds) {
                return Number(seconds);
            }
        }

        return screenRecordingLimit;
    }, [search, isNonProductionEnvironment, screenRecordingLimit]);

    const timeoutIdRef = useRef<number>();
    const tick = useCallback(
        (startTime: number) => {
            const seconds = Math.round((Date.now() - startTime) / 1000);

            if (limitSeconds && seconds >= limitSeconds) {
                actions.stop({ reason: 'after_30_min' });
            }

            timeoutIdRef.current = window.setTimeout(() => {
                // запускаем интервал и сохраняем id таймаута для дальнейшей очистки
                tick(startTime);
            }, 1000);
        },
        [actions, limitSeconds]
    );

    useEffect(() => {
        if (recordStartTime) {
            tick(recordStartTime);
        } else {
            clearTimeout(timeoutIdRef.current);
        }
        return () => clearTimeout(timeoutIdRef.current);
    }, [recordStartTime, tick]);

    useEffect(() => {
        if (limitSeconds && !hideRecordingLimitPopup && !isShownLimitMessage && !isShowLimitationPopup) {
            toggleLimitationPopup();
        }
    }, [limitSeconds, hideRecordingLimitPopup, isShownLimitMessage, isShowLimitationPopup, toggleLimitationPopup]);

    const closeLimitationPopup = useCallback(() => {
        if (isShowLimitationPopup) {
            toggleLimitationPopup();
        }
    }, [isShowLimitationPopup, toggleLimitationPopup]);

    // Кнопка записи показывается и скрывается при запуске и остановке записи
    // в это время на кнопке трансляции может быть обучающий тултип который должен
    // сдвинуться вместе с анкором при появлении/скрытии кнопки записи
    useEffect(() => {
        dispatchOnDocumentChange();

        return () => {
            setTimeout(() => dispatchOnDocumentChange(), 0);
        };
    }, []);

    return (
        <>
            <RecordLimitationPopup visible={isShowLimitationPopup} anchor={ref} onClose={closeLimitationPopup} />
            <div ref={ref} className={classes.buttonContainer}>
                <BaseStopRecordingButton recordingPlace="local" />
            </div>
        </>
    );
}
